// src/context/AuthContext.js
// -----------------------------------------------------------------------------
// AuthProvider
// Changements clés :
// - Normalisation du `user.role` en uppercase dès la lecture (localStorage et /users/me).
// - Garde-fou pour ne PAS appeler /users/me/ pendant un flux de login (__AUTH_LOGIN_FLOW__).
// - logout() : nettoyage + redirection hard vers /authentication/sign-in pour repartir propre.
// -----------------------------------------------------------------------------

import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import api from "api/axios";
import { normalizeRole } from "context/roles";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const devTenant =
    process.env.REACT_APP_DEV_TENANT &&
    (window.location.hostname.endsWith("lvh.me") ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
      ? process.env.REACT_APP_DEV_TENANT
      : null;

  // ---- 1) Lire ?company=... une seule fois et épurer l'URL
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const qCompany = url.searchParams.get("company");
      if (qCompany) {
        localStorage.setItem("company", qCompany);
        setCompany(qCompany);
        url.searchParams.delete("company");
        const qs = url.searchParams.toString();
        window.history.replaceState({}, "", url.pathname + (qs ? `?${qs}` : ""));
      }
    } catch (e) {
      console.warn("⚠️ Impossible de parser l'URL pour ?company=", e);
    }
  }, []);

  // ---- 2) Hydrater depuis localStorage (user + company)
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedCompany = localStorage.getItem("company");

    if (storedCompany) {
      setCompany(storedCompany);
    } else if (devTenant) {
      localStorage.setItem("company", devTenant);
      setCompany(devTenant);
    }

    if (storedUser) {
      try {
        const u = JSON.parse(storedUser);
        // 🔁 Normaliser le rôle en uppercase ici
        const normalized = u ? { ...u, role: normalizeRole(u.role) } : null;
        setUser(normalized);
        setIsAuthenticated(Boolean(normalized));
      } catch (e) {
        console.error("❌ Erreur parsing user depuis localStorage :", e);
        localStorage.removeItem("user");
      }
    }
  }, [devTenant]);

  // ---- 3) Bootstrap /users/me/ (si on a un tenant)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!company) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      // ✅ NE PAS bootstrapper /users/me/ sur les pages d’auth (évite un 401 bruyant)
      const path = window.location?.pathname || "";
      const onAuthPage = path.startsWith("/authentication/");

      // ✅ NE PAS bootstrapper pendant un flux de login (SignIn pose ce flag)
      const loginFlow = Boolean(window.__AUTH_LOGIN_FLOW__);

      if (onAuthPage || loginFlow) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      try {
        const { data } = await api.get("/users/me/");
        if (!cancelled && data && data.id) {
          const normalized = { ...data, role: normalizeRole(data.role) };
          setUser(normalized);
          setIsAuthenticated(true);
          localStorage.setItem("user", JSON.stringify(normalized));
        } else if (!cancelled) {
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem("user");
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setIsAuthenticated(false);
          localStorage.removeItem("user");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company]);

  // ---- API exposée
  const login = (userData, companyName) => {
    if (companyName) {
      localStorage.setItem("company", companyName);
      setCompany(companyName);
    }
    if (userData) {
      const normalized = { ...userData, role: normalizeRole(userData.role) };
      localStorage.setItem("user", JSON.stringify(normalized));
      localStorage.setItem("user_id", normalized.id);
      setUser(normalized);
      setIsAuthenticated(true);
    }
  };

  const logout = async () => {
    // Optionnel: informer le backend (si endpoint disponible)
    try {
      await api.post("/public_auth/logout/");
    } catch (_) {
      // silencieux : on ne bloque pas le logout client
    }

    // Nettoyage local
    localStorage.removeItem("user");
    localStorage.removeItem("company");
    localStorage.removeItem("user_id");
    setUser(null);
    setCompany(null);
    setIsAuthenticated(false);

    // 🔁 Redirection "hard" pour repartir propre (évite un retour à une page protégée via historique)
    window.location.replace("/authentication/sign-in");
  };

  const value = useMemo(
    () => ({
      user,
      company,
      isAuthenticated,
      isLoading,
      login,
      logout,
    }),
    [user, company, isAuthenticated, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
